import { ENV } from '@/app/common/constants'
import { parseRequestError } from '@/app/common/utilities'
import {
  MarketplaceAffiliationPaginated,
  MarketplaceAffiliationPaginatedSchema,
  MarketplaceStore,
  MarketplaceStorePaginated,
  MarketplaceStorePaginatedSchema,
  MarketplaceStoreSchema,
} from '@/queries/marketplace/schema'
import axios from 'axios'
import { MarketplaceItemListSchema } from '../marketplace/marketplace.schema'
import { MarketplacePreviewItemsParams } from './hooks/useMarketplacePreviewItems'
import { MarketplaceAccessMode } from './types'

export type MarketplacePreviewStoresParams = {
  perPage: number
  page: number
  search: string
  business_type: string
}

export type MarketplacePreviewAffiliationsParams = {
  perPage: number
  page: number
  search: string
}

export const marketplacePreviewAxiosInstance = axios.create({
  baseURL: ENV.VITE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  params: {
    global_on_id: ENV.VITE_WALLET_GLOBAL_ON_ID,
  },
})

const getMarketplaceAccessEndpoint = (mode: MarketplaceAccessMode) => {
  return mode === 'published'
    ? '/preview/marketplace_view'
    : '/preview/marketplace_preview'
}

// The store scoped preview answers with a single store, while the published
// marketplace answers with a paginated list. Normalize both into one shape so
// the UI only has to deal with a paginated response.
const isPaginatedResponse = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray((data as { data?: unknown }).data)

const parseStoresResponse = (data: unknown): MarketplaceStorePaginated => {
  if (isPaginatedResponse(data)) {
    return MarketplaceStorePaginatedSchema.parse(data)
  }

  const singleStore = MarketplaceStoreSchema.parse(data)

  return {
    current_page: 1,
    data: [singleStore],
    first_page_url: '',
    from: 1,
    last_page: 1,
    last_page_url: '',
    links: [],
    next_page_url: null,
    path: '',
    per_page: 1,
    prev_page_url: null,
    to: 1,
    total: 1,
  }
}

export const GETMarketplacePreviewItems = async (
  params: MarketplacePreviewItemsParams,
  mode: MarketplaceAccessMode = 'preview'
) => {
  try {
    const response = await marketplacePreviewAxiosInstance.get(
      `${getMarketplaceAccessEndpoint(mode)}/items`,
      {
        params: {
          per_page: params.perPage,
          page: params.page,
          search: params.search,
          ...(params.business_type && { business_type: params.business_type }),
          affiliation_id: params.affiliation_id,
          provider_id: params.provider_id,
        },
      }
    )

    return MarketplaceItemListSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const GETMarketplacePreviewStores = async (
  params: MarketplacePreviewStoresParams,
  mode: MarketplaceAccessMode = 'preview'
): Promise<MarketplaceStorePaginated> => {
  try {
    const response = await marketplacePreviewAxiosInstance.get(
      `${getMarketplaceAccessEndpoint(mode)}/stores`,
      {
        params: {
          per_page: params.perPage,
          page: params.page,
          search: params.search,
          ...(params.business_type && { business_type: params.business_type }),
        },
      }
    )

    return parseStoresResponse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const GETMarketplacePreviewAffiliations = async (
  params: MarketplacePreviewAffiliationsParams,
  mode: MarketplaceAccessMode = 'preview'
): Promise<MarketplaceAffiliationPaginated> => {
  try {
    const response = await marketplacePreviewAxiosInstance.get(
      `${getMarketplaceAccessEndpoint(mode)}/affiliations`,
      {
        params: {
          per_page: params.perPage,
          page: params.page,
          ...(params.search && { search: params.search }),
        },
      }
    )

    return MarketplaceAffiliationPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

// The stores endpoint cannot be queried by id, so the store is looked up by
// walking the pages. The cap only guards against a runaway loop if the
// response ever reports an unexpected last_page.
const STORE_LOOKUP_PER_PAGE = 100
const STORE_LOOKUP_MAX_PAGES = 20

export const GETMarketplacePreviewStoreById = async (
  providerId: number,
  mode: MarketplaceAccessMode = 'preview'
): Promise<MarketplaceStore | null> => {
  let page = 1
  let lastPage = 1

  do {
    const stores = await GETMarketplacePreviewStores(
      {
        perPage: STORE_LOOKUP_PER_PAGE,
        page,
        search: '',
        business_type: '',
      },
      mode
    )

    const store = stores.data.find((item) => item.id === providerId)

    if (store) {
      return store
    }

    lastPage = stores.last_page
    page += 1
  } while (page <= lastPage && page <= STORE_LOOKUP_MAX_PAGES)

  return null
}
